import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CheckCircle2,
  FileText,
  Loader2,
  RefreshCcw,
  ShieldCheck,
  UploadCloud,
  XCircle,
  AlertCircle,
  PartyPopper,
  Eye,
  Trash2,
  ArrowRight,
  Clock,
  Check,
  AlertTriangle,
  BellRing,
  X,
} from "lucide-react";

import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/Modal";
import { ComplianceService } from "@/services/complianceService";
import useLiveResource from "@/hooks/useLiveResource";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import ChecklistChangeNotice from "./components/ChecklistChangeNotice";

const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png";
const ACCEPTED_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Slot states a candidate can (re-)upload into: nothing yet, or re-requested.
//
// `uploadable` is the server's word on whether the slot can still receive a
// file at all. It is false when the candidate is pinned to a checklist version
// whose subsection has since been renamed or removed: the requirement stands
// and any earlier upload stays visible, but the id needed to post a new file no
// longer exists, so offering the control would be a dead end. Defaults to true
// so a slot from an older response behaves exactly as before.
const canUpload = (slot) => (slot?.uploadable ?? true) && (!slot?.status || slot.status === "re_requested");

// Types the inline viewer can render. Anything else (a PDF on a browser with no
// built-in viewer, say) still opens — the object URL is handed to a new tab
// rather than embedded, so the candidate is never left with a dead button.
const INLINE_VIEWABLE = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

// Upload attestation (Phase 8C).
//
// ⚠️ PLACEHOLDER COPY, pending sign-off. One constant so a reword is one line —
// the server stores the wording actually displayed on each consent row, so
// editing this does not rewrite what past uploaders agreed to. The backend has
// its own copy (schema/api.py) and that is the one persisted; keep them in step.
const CANDIDATE_ATTESTATION =
  "I consent to the collection, use, processing, storage, and sharing of the " +
  "personal data provided by me for compliance verification purposes. I " +
  "acknowledge that AI or automated tools may be used as part of the compliance " +
  "review process, and that all final decisions will be subject to human review. " +
  "I confirm that the information and documents provided are accurate and that " +
  "I am authorized to submit them in accordance with applicable data protection " +
  "laws.";

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

const validateFile = (file) => {
  if (!ACCEPTED_MIME.includes(file.type)) {
    return `"${file.name}" isn't a supported file type. Please upload a PDF, JPG, or PNG.`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" is too large. Max file size is ${MAX_FILE_SIZE_MB}MB.`;
  }
  return null;
};

/**
 * Candidate self-service upload portal.
 *
 * Public token route (/compliance-portal/:token — no login, the token is the
 * credential). Lets the candidate stage documents against their checklist
 * slots, review everything in one confirmation step, then submit as a batch —
 * uploads flow into the same ComplianceDocument pipeline (auto-extraction,
 * officer review). Supports flexible partial submissions with reminders to
 * complete remaining documents on time.
 */
const CompliancePortal = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [portal, setPortal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingSlotId, setUploadingSlotId] = useState(null);

  // subsectionId -> staged file object: { file, name, size, type, previewUrl, formattedSize, slotName, sectionName }
  const [stagedFiles, setStagedFiles] = useState({});
  // subsectionId -> error string
  const [slotErrors, setSlotErrors] = useState({});
  // subsectionId currently being dragged over
  const [dragOverSlotId, setDragOverSlotId] = useState(null);

  // Modals & views
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  // Attestation (Phase 8C). A persistent checkbox rather than a per-upload
  // modal: this page is drag-and-drop first, and interrupting a drop with a
  // dialog would be worse than stating the condition up front. Deliberately NOT
  // remembered across reloads — it is an attestation, not a preference, and the
  // server records one consent row per document actually submitted.
  const [attested, setAttested] = useState(false);

  const fileInputRef = useRef(null);
  const pendingSlotRef = useRef(null);

  const loadPortal = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const data = await ComplianceService.getPortal(token);
        setPortal(data);
        setErrorMessage("");
      } catch (error) {
        setPortal(null);
        setErrorMessage(apiErrorMessageFormat(error, "This link is invalid or no longer available."));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (!token) {
      setErrorMessage("This link is invalid or no longer available.");
      setLoading(false);
      return;
    }
    loadPortal();
  }, [token, loadPortal]);

  // Live updates over the magic-link socket.
  //
  // The officer can change what this page asks for while the candidate is
  // looking at it — re-applying an edited checklist adds documents, removes
  // others, and sends approved ones back for review. Without this the page
  // kept showing the old list until the candidate happened to reload, and a
  // document they had already uploaded silently vanished from it.
  //
  // The whole `compliance_candidate` namespace rather than one id: a link
  // connection is matched ONLY by its own candidate (ws_manager
  // `link_candidate_ids`), so it cannot receive anyone else's events, and the
  // portal payload carries no compliance-candidate id to narrow with.
  //
  // A refetch, never applied state — a dropped event leaves this page stale
  // until the next fetch, never wrong.
  useLiveResource({
    keys: "compliance_candidate",
    transport: "link",
    token,
    enabled: Boolean(token),
    onChange: () => loadPortal({ silent: true }),
  });

  // Clean up staged-file object URLs on unmount only. A `[stagedFiles]` dep
  // here would re-run this effect on every stage/unstage — and since the
  // cleanup closes over whichever `stagedFiles` snapshot was current when it
  // was scheduled, staging a *new* file would revoke every *other* file's
  // still-in-use preview URL right out from under it. The ref always holds
  // the latest snapshot for the one cleanup that actually runs, on unmount.
  const stagedFilesRef = useRef(stagedFiles);
  useEffect(() => {
    stagedFilesRef.current = stagedFiles;
  }, [stagedFiles]);

  useEffect(() => {
    return () => {
      Object.values(stagedFilesRef.current).forEach((staged) => {
        if (staged?.previewUrl) URL.revokeObjectURL(staged.previewUrl);
      });
    };
  }, []);

  // Object URLs are a memory leak if they outlive the element using them, and
  // the browser holds the whole blob until they are revoked. Only revoke here
  // when the open preview was fetched from the server (`revokeOnClose`) — a
  // staged file's preview URL is owned by `stagedFiles` and revoked when it is
  // unstaged or on unmount instead.
  useEffect(() => {
    return () => {
      if (previewDoc?.revokeOnClose && previewDoc?.url) URL.revokeObjectURL(previewDoc.url);
    };
  }, [previewDoc]);

  const closePreviewDoc = useCallback(() => {
    setPreviewDoc((current) => {
      if (current?.revokeOnClose && current.url) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  // Preview a document already sent to the server (Phase 8B) — fetched on
  // demand as a blob, since the portal has no persistent access to it otherwise.
  const openExistingDocPreview = useCallback(
    async (slot) => {
      if (!slot?.documentId || previewLoadingId) return;
      setPreviewLoadingId(slot.documentId);
      try {
        const response = await ComplianceService.fetchPortalDocumentFile(token, slot.documentId);
        const type = response?.data?.type || "application/octet-stream";
        const url = URL.createObjectURL(response.data);
        if (INLINE_VIEWABLE.includes(type)) {
          setPreviewDoc({
            name: slot.originalFilename || slot.name,
            url,
            type,
            revokeOnClose: true,
          });
        } else {
          // Unknown type: hand it to the browser rather than embedding
          // something it cannot render and showing a blank frame. Not revoked
          // immediately — the new tab still needs it.
          window.open(url, "_blank", "noopener");
        }
      } catch (e) {
        console.error(e);
        toast.error(apiErrorMessageFormat(e, "Could not open that document. Please try again."));
      } finally {
        setPreviewLoadingId(null);
      }
    },
    [token, previewLoadingId],
  );

  const handleStageFile = (slot, file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setSlotErrors((prev) => ({ ...prev, [slot.subsectionId]: validationError }));
      toast.error(validationError);
      return;
    }

    setSlotErrors((prev) => {
      const next = { ...prev };
      delete next[slot.subsectionId];
      return next;
    });

    if (stagedFiles[slot.subsectionId]?.previewUrl) {
      URL.revokeObjectURL(stagedFiles[slot.subsectionId].previewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setStagedFiles((prev) => ({
      ...prev,
      [slot.subsectionId]: {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl,
        formattedSize: formatBytes(file.size),
        slotName: slot.name,
        sectionName: slot.sectionName,
        isRequired: slot.isRequired,
      },
    }));

    toast.success(`Attached ${file.name} for ${slot.name}`);
  };

  const handleCancelStagedFile = (subsectionId) => {
    if (stagedFiles[subsectionId]?.previewUrl) {
      URL.revokeObjectURL(stagedFiles[subsectionId].previewUrl);
    }
    setStagedFiles((prev) => {
      const next = { ...prev };
      delete next[subsectionId];
      return next;
    });
  };

  const openFilePicker = (slot) => {
    pendingSlotRef.current = slot;
    fileInputRef.current?.click();
  };

  const handleFileChosen = (event) => {
    const file = event.target.files?.[0];
    const slot = pendingSlotRef.current;
    event.target.value = "";
    pendingSlotRef.current = null;
    if (!file || !slot) return;
    handleStageFile(slot, file);
  };

  const handleDrop = (event, slot) => {
    event.preventDefault();
    setDragOverSlotId(null);
    if (!canUpload(slot) || isSubmitting) return;
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    handleStageFile(slot, file);
  };

  // ── Metrics & Calculations ──────────────────────────────────────────────────

  const slots = portal?.slots ?? [];
  const removedDocuments = portal?.removedDocuments ?? [];
  // Documents the edit started asking for. Derived on the server from the
  // version snapshot the candidate is pinned to, so it survives a reload and a
  // socket that was down when the officer saved — the same rule
  // removedDocuments follows.
  const addedDocuments = portal?.addedDocuments ?? [];

  // A slot is validly complete if already uploaded & not re-requested, or if currently staged
  const isSlotDone = (s) => (s.status && s.status !== "re_requested") || Boolean(stagedFiles[s.subsectionId]);

  const completedCount = slots.filter(isSlotDone).length;
  const requiredSlots = slots.filter((s) => s.isRequired);
  const missingRequiredSlots = requiredSlots.filter((s) => !isSlotDone(s));

  const allRequiredDone = requiredSlots.length === 0 || missingRequiredSlots.length === 0;
  const stagedCount = Object.keys(stagedFiles).length;
  const progressPct = slots.length ? Math.round((completedCount / slots.length) * 100) : 0;

  const handleOpenSubmitModal = () => {
    if (stagedCount === 0) {
      toast.error("Please attach at least one document before submitting.");
      return;
    }
    if (!attested) {
      toast.error("Please tick the confirmation checkbox before submitting.");
      return;
    }
    setConfirmModalOpen(true);
  };

  const handleConfirmSubmitAll = async () => {
    const stagedEntries = Object.entries(stagedFiles);
    if (stagedEntries.length === 0) {
      toast.error("No staged documents to submit.");
      return;
    }
    if (!attested) {
      toast.error("Please tick the confirmation checkbox before submitting.");
      return;
    }

    setIsSubmitting(true);
    setConfirmModalOpen(false);

    let successCount = 0;

    for (let i = 0; i < stagedEntries.length; i++) {
      const [subsectionId, staged] = stagedEntries[i];
      setUploadingSlotId(subsectionId);
      try {
        await ComplianceService.uploadPortalDocument(token, {
          file: staged.file,
          subsectionId,
          attestationAccepted: true,
        });
        successCount++;
      } catch (error) {
        const message = apiErrorMessageFormat(error, `Failed to upload ${staged.name}`);
        setSlotErrors((prev) => ({ ...prev, [subsectionId]: message }));
        toast.error(message);
      }
    }

    setUploadingSlotId(null);
    setIsSubmitting(false);

    if (successCount > 0) {
      toast.success(successCount === 1 ? "Document submitted successfully!" : `${successCount} documents submitted successfully!`);
      Object.values(stagedFiles).forEach((staged) => {
        if (staged?.previewUrl) URL.revokeObjectURL(staged.previewUrl);
      });
      setStagedFiles({});
      await loadPortal({ silent: true });
      setShowSuccessScreen(true);
    }
  };

  // Group slots by section, preserving order
  const sections = [];
  const sectionIndex = new Map();
  for (const slot of slots) {
    if (!sectionIndex.has(slot.sectionName)) {
      sectionIndex.set(slot.sectionName, sections.length);
      sections.push({ name: slot.sectionName, slots: [] });
    }
    sections[sectionIndex.get(slot.sectionName)].slots.push(slot);
  }

  // Pending slots for the post-submission screen
  const remainingPendingSlots = slots.filter((s) => !s.status || s.status === "re_requested");
  const remainingRequiredSlots = remainingPendingSlots.filter((s) => s.isRequired);

  // ── Loading / Invalid-link states ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-4 bg-background-primary">
        <Card className="max-w-md w-full p-8 text-center">
          <Loader2 size={40} className="mx-auto mb-4 animate-spin text-text-secondary" />
          <h2 className="text-lg font-semibold text-text-primary">Loading your document checklist…</h2>
        </Card>
      </div>
    );
  }

  if (errorMessage || !portal) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-4 bg-background-primary">
        <Card className="max-w-md w-full p-8 text-center">
          <XCircle size={64} className="mx-auto text-tone-rose mb-4" />
          <h2 className="text-xl text-text-primary font-semibold mb-2">Link unavailable</h2>
          <p className="text-text-secondary">{errorMessage || "This link is invalid or no longer available."}</p>
        </Card>
      </div>
    );
  }

  // ── Success Confirmation Screen ───────────────────────────────────────────

  if (showSuccessScreen) {
    const hasPendingRemaining = remainingPendingSlots.length > 0;

    return (
      <div className="min-h-[85vh] bg-background-primary px-4 sm:px-6 py-10">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-tone-emerald/10 border border-tone-emerald/30 flex items-center justify-center mx-auto mb-4 text-tone-emerald">
              <CheckCircle2 size={36} />
            </div>

            <h1 className="text-2xl font-bold text-text-primary">
              {hasPendingRemaining ? "Documents Submitted Successfully!" : "All Documents Submitted Successfully!"}
            </h1>
            <p className="text-text-secondary text-sm mt-2 max-w-lg mx-auto">
              Thank you, {portal.candidateName || "there"}. Your attached documents have been received by the {portal.companyName ? `${portal.companyName} ` : ""}
              compliance team for review.
            </p>

            {/* Reminder Callout to Submit Remaining Documents on Time */}
            {hasPendingRemaining && (
              <div className="mt-6 p-4 rounded-2xl bg-tone-orange/10 border border-tone-orange/30 text-left">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-tone-orange shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-text-primary">Please remember to submit your remaining documents on time</p>
                    <p className="text-xs text-text-secondary">
                      You still have{" "}
                      <span className="font-semibold text-text-primary">
                        {remainingPendingSlots.length} document{remainingPendingSlots.length > 1 ? "s" : ""}
                      </span>{" "}
                      {remainingRequiredSlots.length > 0 && `(${remainingRequiredSlots.length} required)`} pending. To ensure timely onboarding and verification, please
                      upload them as soon as possible.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Every slot with its outcome, not just the ones still missing.
                The candidate has just uploaded and has no other record of what
                landed; a list of only the gaps leaves them guessing whether the
                rest went through. Scrolls at ~4 rows so the progression guide
                below stays on screen. */}
            {slots.length > 0 && (
              <div className="mt-6 p-4 sm:p-5 rounded-2xl bg-background-secondary border border-border-primary text-left">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-xs font-semibold text-text-secondary">Document Submission Status</h2>
                  <span className="text-xs text-text-muted font-medium">
                    {slots.length - remainingPendingSlots.length} of {slots.length} Submitted
                  </span>
                </div>

                <div className="divide-y divide-border-primary rounded-xl border border-border-primary bg-background-primary overflow-y-auto max-h-[210px] overscroll-contain">
                  {slots.map((s) => {
                    const isSubmitted = Boolean(s.status && s.status !== "re_requested");
                    const isReRequested = s.status === "re_requested";

                    return (
                      <div key={s.subsectionId} className="flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {isSubmitted ? <CheckCircle2 size={16} className="text-tone-emerald shrink-0" /> : <XCircle size={16} className="text-tone-rose shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-medium text-text-primary truncate text-xs sm:text-sm flex items-center gap-1">
                              <span>{s.name}</span>
                              {s.isRequired && (
                                <span className="text-tone-rose" title="Required">
                                  *
                                </span>
                              )}
                            </p>
                            {s.sectionName && <p className="text-[11px] text-text-muted truncate">{s.sectionName}</p>}
                          </div>
                        </div>

                        <div className="shrink-0">
                          {isSubmitted ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-tone-emerald/10 text-tone-emerald border border-tone-emerald/20">
                              Submitted
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-tone-rose/10 text-tone-rose border border-tone-rose/20">
                              {isReRequested ? "Re-upload Needed" : "Pending"}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Timeline / Status Tracker (No day commitments) */}
            <div className="mt-6 p-5 rounded-2xl bg-background-secondary border border-border-primary text-left">
              <h2 className="text-xs font-semibold text-text-secondary mb-4">What happens next?</h2>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-tone-emerald flex items-center justify-center text-text-invert shrink-0 mt-0.5">
                    <Check size={14} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Documents Received</p>
                    <p className="text-xs text-text-secondary">Your uploaded files have been safely queued for processing.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-tone-blue flex items-center justify-center text-text-invert shrink-0 mt-0.5">
                    <Clock size={14} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Compliance Officer Review</p>
                    <p className="text-xs text-text-secondary">Our team is currently reviewing your submissions.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-tone-orange flex items-center justify-center text-text-invert shrink-0 mt-0.5">
                    <BellRing size={13} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Notification & Updates</p>
                    <p className="text-xs text-text-secondary">You will be notified if any document is re-requested or needs revision.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-3">
              <Button
                onClick={() => setShowSuccessScreen(false)}
                className="px-5 py-2.5 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 cursor-pointer"
              >
                {hasPendingRemaining ? "Upload Remaining Documents" : "View Document Checklist"}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ── Main Portal View (Sections in Grid, Documents in List View) ───────────

  return (
    <div className="min-h-[85vh] bg-background-primary px-4 sm:px-6 py-8 pb-36">
      <Input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} className="hidden" onChange={handleFileChosen} />

      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header Card */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-text-primary">Hi {portal.candidateName || "there"},</h1>
              <p className="text-text-secondary text-sm mt-1">
                We need a few documents from you to complete verification
                {portal.checklistName ? ` for “${portal.checklistName}”` : ""}
                {portal.companyName ? ` at ${portal.companyName}` : ""}.
              </p>
              <p className="text-text-secondary/80 text-xs mt-2">Accepted formats: PDF, JPG, PNG · Max {MAX_FILE_SIZE_MB}MB per file</p>
            </div>
          </div>

          {/* Progress Bar (Drops back if documents are re-requested) */}
          {slots.length > 0 && (
            <div className="mt-5">
              <div className="flex justify-between text-xs text-text-secondary mb-1.5">
                <span>
                  {completedCount} of {slots.length} documents completed
                </span>
                <span className="font-semibold text-text-primary">{progressPct}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-background-secondary border border-border-primary/50 overflow-hidden">
                {progressPct > 0 && <div className="h-full rounded-full bg-tone-emerald transition-none" style={{ width: `${progressPct}%` }} />}
              </div>
            </div>
          )}

          {/* Legend & Completion Notice */}
          {requiredSlots.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-text-secondary">
              <span>
                <span className="text-tone-rose font-semibold">*</span> indicates a required document
              </span>
              {missingRequiredSlots.length > 0 ? (
                <span className="text-tone-orange font-medium">
                  {missingRequiredSlots.length} required document{missingRequiredSlots.length > 1 ? "s" : ""} still needed
                </span>
              ) : (
                <span className="text-tone-emerald font-medium">All required documents ready</span>
              )}
            </div>
          )}

          {allRequiredDone && !stagedCount && portal.status !== "pending_from_candidate" && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-tone-emerald/10 border border-tone-emerald/30 px-3.5 py-2.5 text-sm text-tone-emerald">
              <PartyPopper size={16} />
              <span>All required documents received — our compliance team is reviewing your submission.</span>
            </div>
          )}

          {/* What a checklist edit changed for this candidate.
              
              Both halves, in two groups. Said out loud rather than letting
              documents appear and disappear: a list quietly longer or shorter
              than last time reads as "the site lost my upload", and "some
              documents" invites them to go checking which. Added comes first
              because it is the only half with anything to do. */}
          <ChecklistChangeNotice addedDocuments={addedDocuments} removedDocuments={removedDocuments} />
        </Card>

        {/* Upload attestation (Phase 8C). Stated once, above the slots, rather
            than interrupting each drop with a dialog — this page is
            drag-and-drop first. Only rendered when there is still something to
            upload: asking someone to attest when every slot is settled is
            noise. */}
        {slots.some((s) => canUpload(s)) && (
          <Card className="p-4">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Input
                type="checkbox"
                className="w-auto h-auto mt-1 size-3 shrink-0 p-0 rounded accent-tone-blue cursor-pointer"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
              />
              <span className="text-[13px] text-text-primary">{CANDIDATE_ATTESTATION}</span>
            </label>
            <p className="text-xs text-text-secondary mt-2">Tick this before submitting. Your confirmation is recorded with each document you send.</p>
          </Card>
        )}

        {/* SECTIONS IN GRID LAYOUT (1 Column on Mobile, 2 Columns on Desktop) */}
        {slots.length === 0 ? (
          <Card className="p-6 text-center text-text-secondary">Your document list is being prepared — please check back later.</Card>
        ) : (
          <div className={`grid grid-cols-1 ${sections.length > 1 ? "lg:grid-cols-2" : "lg:grid-cols-1"} gap-6 items-start`}>
            {sections.map((section) => {
              const sectionSlotsDone = section.slots.filter(isSlotDone).length;
              return (
                <Card key={section.name} className="p-5 flex flex-col h-full">
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4 pb-2.5 border-b border-border-primary/60">
                    <h2 className="text-sm font-semibold text-text-primary">{section.name}</h2>
                    <span className="text-xs text-text-secondary font-medium">
                      {sectionSlotsDone} of {section.slots.length} attached
                    </span>
                  </div>

                  {/* DOCUMENTS IN VERTICAL LIST VIEW */}
                  <div className="space-y-3 flex-1">
                    {section.slots.map((slot) => {
                      const staged = stagedFiles[slot.subsectionId];
                      const uploading = uploadingSlotId === slot.subsectionId;
                      const uploadable = canUpload(slot);
                      const error = slotErrors[slot.subsectionId];
                      const isDragOver = dragOverSlotId === slot.subsectionId;
                      const previewingExisting = previewLoadingId === slot.documentId;

                      return (
                        <div
                          key={slot.subsectionId}
                          onDragOver={(e) => {
                            if (!uploadable || isSubmitting) return;
                            e.preventDefault();
                            setDragOverSlotId(slot.subsectionId);
                          }}
                          onDragLeave={() => setDragOverSlotId((current) => (current === slot.subsectionId ? null : current))}
                          onDrop={(e) => handleDrop(e, slot)}
                          className={[
                            "rounded-xl border p-3.5 flex flex-col gap-2.5 transition-colors",
                            error
                              ? "border-tone-rose/50 bg-tone-rose/5"
                              : staged
                                ? "border-tone-emerald/50 bg-tone-emerald/5"
                                : isDragOver
                                  ? "border-tone-emerald bg-tone-emerald/5"
                                  : slot.status === "re_requested"
                                    ? "border-tone-orange/50 bg-tone-orange/5"
                                    : "border-border-primary bg-background-secondary/30",
                          ].join(" ")}
                        >
                          {/* Row 1: Icon, Document Name, Required Indicator */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="shrink-0 flex items-center text-text-secondary">
                                {slot.status === "approved" ? (
                                  <CheckCircle2 size={17} className="text-tone-emerald" />
                                ) : staged ? (
                                  <CheckCircle2 size={17} className="text-tone-emerald" />
                                ) : error ? (
                                  <AlertCircle size={17} className="text-tone-rose" />
                                ) : slot.status === "re_requested" ? (
                                  <AlertCircle size={17} className="text-tone-orange" />
                                ) : (
                                  <FileText size={17} />
                                )}
                              </div>

                              <div className="min-w-0">
                                <p className="text-[13px] font-medium text-text-primary leading-tight">
                                  {slot.name}
                                  {slot.isRequired && (
                                    <span className="text-tone-rose ml-1" title="Required document">
                                      *
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>

                            <div className="shrink-0">
                              {staged ? (
                                <div className="flex items-center gap-1.5">
                                  <Button
                                    type="button"
                                    onClick={() =>
                                      setPreviewDoc({
                                        name: staged.name,
                                        url: staged.previewUrl,
                                        type: staged.type,
                                        revokeOnClose: false,
                                      })
                                    }
                                    className="inline-flex items-center gap-1 rounded-lg border border-border-primary bg-background-secondary px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-background-icon/60 cursor-pointer"
                                  >
                                    <Eye size={12} />
                                    <span>Preview</span>
                                  </Button>

                                  <Button
                                    type="button"
                                    onClick={() => handleCancelStagedFile(slot.subsectionId)}
                                    className="inline-flex items-center gap-1 rounded-lg border border-tone-rose/30 bg-tone-rose/10 px-2.5 py-1 text-xs font-medium text-tone-rose hover:bg-tone-rose/20 cursor-pointer"
                                  >
                                    <Trash2 size={12} />
                                    <span>Remove</span>
                                  </Button>

                                  <Button
                                    type="button"
                                    onClick={() => openFilePicker(slot)}
                                    className="inline-flex items-center gap-1 rounded-lg border border-border-primary bg-background-icon/40 px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-background-icon/70 cursor-pointer"
                                  >
                                    <RefreshCcw size={12} />
                                    <span>Change</span>
                                  </Button>
                                </div>
                              ) : slot.documentId ? (
                                <Button
                                  type="button"
                                  disabled={previewingExisting}
                                  onClick={() => openExistingDocPreview(slot)}
                                  className="inline-flex items-center gap-1 rounded-lg border border-border-primary bg-background-secondary px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-background-icon/60 disabled:opacity-50 cursor-pointer"
                                >
                                  {previewingExisting ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                                  <span>View</span>
                                </Button>
                              ) : slot.isRequired ? (
                                <span className="text-[11px] text-tone-rose font-medium">Required</span>
                              ) : (
                                <span className="text-[11px] text-text-secondary">Optional</span>
                              )}
                            </div>
                          </div>

                          {/* Row 2: Staged File / Original Filename / Re-request Reason */}
                          {staged && (
                            <div className="p-2 rounded-lg bg-background-primary border border-tone-emerald/30 text-xs text-text-primary flex items-center justify-between">
                              <span className="truncate font-medium">{staged.name}</span>
                              <span className="text-text-secondary text-[11px] ml-2 shrink-0">{staged.formattedSize}</span>
                            </div>
                          )}

                          {!staged && slot.originalFilename && (
                            <p className="text-xs text-text-secondary truncate">
                              Received: <span className="text-text-primary">{slot.originalFilename}</span>
                            </p>
                          )}

                          {slot.status === "re_requested" && (
                            <div className="p-2 rounded-lg bg-tone-orange/10 border border-tone-orange/30 text-xs text-tone-orange">
                              <p className="font-semibold">Re-upload Requested:</p>
                              <p className="mt-0.5 whitespace-normal break-words">{slot.rerequestDescription || "Please provide an updated copy of this document."}</p>
                            </div>
                          )}

                          {error && <p className="text-xs text-tone-rose whitespace-normal break-words">{error}</p>}

                          {/* Row 3: Attach action (uploadable slots only — a staged
                              file's actions and the existing-document View button
                              both live in Row 1, next to the name) */}
                          {!staged && uploadable && (
                            <div className="pt-1.5 border-t border-border-primary/40 flex items-center justify-between">
                              {!error ? <span className="text-[11px] text-text-secondary/70">PDF, JPG, PNG up to {MAX_FILE_SIZE_MB}MB</span> : <span />}

                              <div className="flex items-center gap-1.5 ml-auto">
                                <Button
                                  type="button"
                                  disabled={isSubmitting || !!uploadingSlotId}
                                  onClick={() => openFilePicker(slot)}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-border-primary bg-background-icon/40 px-3 py-1 text-xs font-medium text-text-primary hover:bg-background-icon/70 disabled:opacity-50 cursor-pointer"
                                >
                                  {uploading ? (
                                    <Loader2 size={13} className="animate-spin" />
                                  ) : slot.status === "re_requested" ? (
                                    <RefreshCcw size={13} />
                                  ) : (
                                    <UploadCloud size={13} />
                                  )}
                                  <span>{uploading ? "Submitting…" : slot.status === "re_requested" ? "Attach Replacement" : "Attach File"}</span>
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Bottom Submission Dock */}
      {stagedCount > 0 && (
        <div className="fixed bottom-5 left-0 right-0 z-30 px-4 pointer-events-none">
          <div className="max-w-3xl mx-auto rounded-2xl bg-background-primary/95 border border-border-primary shadow-2xl backdrop-blur-md px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 pointer-events-auto">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-tone-emerald shrink-0" />
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {stagedCount} document{stagedCount > 1 ? "s" : ""} ready for submission
                </p>
                {!allRequiredDone ? (
                  <p className="text-xs text-tone-orange">
                    {missingRequiredSlots.length} required document{missingRequiredSlots.length > 1 ? "s" : ""} pending.
                  </p>
                ) : !attested ? (
                  <p className="text-xs text-tone-orange">Tick the confirmation checkbox above to submit.</p>
                ) : (
                  <p className="text-xs text-tone-emerald">All required documents attached</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <Button
                type="button"
                onClick={() => {
                  Object.values(stagedFiles).forEach((staged) => {
                    if (staged?.previewUrl) URL.revokeObjectURL(staged.previewUrl);
                  });
                  setStagedFiles({});
                }}
                disabled={isSubmitting}
                className="px-3.5 py-2 text-xs font-medium text-text-secondary hover:text-text-primary cursor-pointer"
              >
                Clear All
              </Button>

              <Button
                type="button"
                disabled={isSubmitting || !attested}
                onClick={handleOpenSubmitModal}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-button-secondary text-text-invert text-sm font-medium hover:opacity-90 disabled:opacity-50 cursor-pointer shadow-md"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Submitting Files…</span>
                  </>
                ) : (
                  <>
                    <span>Submit Documents for Review</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Review & Confirmation Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="max-w-lg p-6 space-y-4">
          <DialogHeader className="p-0 pb-3 border-b border-border-primary flex-row items-center justify-between gap-3">
            <DialogTitle className="text-lg font-semibold text-text-primary">Submit Documents for Review</DialogTitle>
            <DialogClose className="shrink-0 rounded-md p-1 text-text-secondary hover:text-text-primary hover:bg-background-icon/40 cursor-pointer">
              <X size={18} />
              <span className="sr-only">Close</span>
            </DialogClose>
          </DialogHeader>

          <p className="text-sm text-text-secondary">
            You are about to submit the following {stagedCount} document{stagedCount > 1 ? "s" : ""} to the compliance office:
          </p>

          {/* Staged files summary */}
          <div className="max-h-48 overflow-y-auto space-y-2 p-2 rounded-xl bg-background-secondary border border-border-primary">
            {Object.entries(stagedFiles).map(([subId, staged]) => (
              <div key={subId} className="flex items-center justify-between text-xs p-2 rounded-lg bg-background-primary border border-border-primary/50">
                <div className="min-w-0 flex items-center gap-2">
                  <FileText size={14} className="text-text-secondary shrink-0" />
                  <span className="font-medium text-text-primary truncate">{staged.slotName}:</span>
                  <span className="text-text-secondary truncate">{staged.name}</span>
                </div>
                <span className="text-text-secondary shrink-0 ml-2">{staged.formattedSize}</span>
              </div>
            ))}
          </div>

          {/* Partial submission reminder if required files are still pending */}
          {!allRequiredDone && (
            <div className="p-3 rounded-xl bg-tone-orange/10 border border-tone-orange/30 text-xs text-tone-orange flex items-start gap-2.5">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Partial Submission Notice:</p>
                <p className="mt-0.5">
                  You still have {missingRequiredSlots.length} required document{missingRequiredSlots.length > 1 ? "s" : ""} pending (
                  {missingRequiredSlots.map((s) => s.name).join(", ")}). You can submit these attached document(s) now, and remember to submit the remaining documents on
                  time to complete your verification.
                </p>
              </div>
            </div>
          )}

          <div className="p-3 rounded-xl bg-background-secondary/60 border border-border-primary text-xs text-text-secondary space-y-1.5">
            <p className="font-semibold text-text-primary">Before submitting, please ensure:</p>
            <p>• All pages are clear, fully visible, and free of glare.</p>
            <p>• The details match your official identification records.</p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              onClick={() => setConfirmModalOpen(false)}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSubmitAll}
              disabled={isSubmitting}
              className="px-5 py-2 rounded-xl bg-button-secondary text-text-invert text-sm font-medium hover:opacity-90 cursor-pointer"
            >
              {isSubmitting ? "Submitting…" : "Confirm & Submit"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Lightbox Modal — used for both a still-staged file
          and one already sent to the server (see `revokeOnClose`). */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && closePreviewDoc()}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden flex flex-col max-h-[90vh]" showCloseButton>
          <DialogHeader className="px-5 py-4 border-b border-border-primary">
            <DialogTitle className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <FileText size={16} className="text-text-secondary" />
              <span>Preview: {previewDoc?.name}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 p-4 overflow-auto bg-background-secondary/40 flex items-center justify-center min-h-[50vh]">
            {previewDoc?.type === "application/pdf" ? (
              <iframe src={previewDoc.url} title={previewDoc.name} className="w-full h-[65vh] rounded-lg border border-border-primary bg-white" />
            ) : (
              <img src={previewDoc?.url} alt={previewDoc?.name} className="max-h-[65vh] max-w-full object-contain rounded-lg shadow-sm" />
            )}
          </div>

          <div className="px-5 py-3 border-t border-border-primary flex justify-end">
            <Button
              type="button"
              onClick={closePreviewDoc}
              className="px-4 py-1.5 rounded-lg bg-background-icon text-text-primary text-xs font-medium hover:bg-background-icon/60 cursor-pointer"
            >
              Close Preview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompliancePortal;
